import { Infinity, KeyRound, LoaderCircle, Scale, ShieldCheck, UserPlus } from "lucide-react";

interface AccessGateProps {
  onLogin: () => void;
  onSignup: () => void;
  onGoogle: () => void;
  authAvailable?: boolean;
  error?: string;
  pendingAction?: "google" | "signup" | "login";
}

export function AccessGate({
  onLogin,
  onSignup,
  onGoogle,
  authAvailable = true,
  error,
  pendingAction,
}: AccessGateProps) {
  const authPending = Boolean(pendingAction);

  return (
    <main className="access-gate">
      <img
        className="access-backdrop"
        src="/login-bastion-hero.jpg"
        alt=""
        aria-hidden="true"
      />
      <div className="access-atmosphere" aria-hidden="true" />

      <section className="access-copy" aria-labelledby="access-title">
        <div className="access-wordmark" aria-label="VAELOR">
          <strong>VAELOR</strong>
          <span>Repousse le Miasme · Conquiers le Cœur</span>
        </div>

        <p className="eyebrow">Le seuil du Bastion</p>
        <h1 id="access-title">Ton Bastion t’attend.</h1>
        <p className="access-lead">
          Entre dans un monde permanent. Fortifie ta cité, rassemble ton armée et ouvre la route vers le Cœur.
        </p>

        <div className="access-principles" aria-label="Les engagements de VAELOR">
          <span><Infinity size={19} aria-hidden="true" /><b>Monde permanent</b></span>
          <span><Scale size={19} aria-hidden="true" /><b>Aucune puissance vendue</b></span>
          <span><ShieldCheck size={19} aria-hidden="true" /><b>Progression conservée</b></span>
        </div>

        {!authAvailable && (
          <div className="notice access-identity-pending" role="status">
            <strong>Inscriptions en préparation</strong>
            <span>Le portail est prêt. La connexion sécurisée de production doit encore être reliée avant d’ouvrir les comptes.</span>
          </div>
        )}
        {error && <div className="notice notice--error" role="alert">{error}</div>}

        <div className="access-auth-actions" aria-label="Créer un compte ou se connecter">
          <button
            className="access-google-command"
            type="button"
            onClick={onGoogle}
            disabled={!authAvailable || authPending}
            data-auth-provider="google-oauth2"
            aria-label="Se connecter ou créer un compte VAELOR avec Google"
          >
            <span className="access-google-logo" aria-hidden="true">
              {pendingAction === "google" ? (
                <LoaderCircle className="spin" size={18} />
              ) : (
                <img src="/google-g.png" alt="" />
              )}
            </span>
            <span>{pendingAction === "google" ? "Ouverture de Google…" : "Se connecter avec Google"}</span>
          </button>
          <button className="text-command access-email-command" type="button" onClick={onSignup} disabled={!authAvailable || authPending}>
            {pendingAction === "signup" ? <LoaderCircle className="spin" size={18} aria-hidden="true" /> : <UserPlus size={18} aria-hidden="true" />}
            {pendingAction === "signup" ? "Ouverture de l’inscription…" : "Créer un compte par e-mail"}
          </button>
          <button className="access-existing-command" type="button" onClick={onLogin} disabled={!authAvailable || authPending}>
            {pendingAction === "login" ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <KeyRound size={16} aria-hidden="true" />}
            {pendingAction === "login" ? "Ouverture de la connexion…" : "J’ai déjà un compte"}
          </button>
        </div>

        <p className="access-security">
          <ShieldCheck size={16} aria-hidden="true" />
          <span>Authentification réelle par Google ou Auth0. VAELOR ne reçoit ni ne conserve ton mot de passe.</span>
        </p>
      </section>
    </main>
  );
}